import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bot, Send, Loader2, Check, MessageSquare, X } from "lucide-react";
import { useOnboardingAssistant } from "./useOnboardingAssistant";

const LABELS = { summary: "ICP summary", entityTypes: "buyer type", industries: "industries", personas: "job titles", seniority: "seniority", geos: "geography", size: "size range",
  signals: "signals", negatives: "exclusions", lookalikeSeeds: "look-alike seeds", companyName: "company", website: "website", products: "products", revenueGoal: "revenue goal",
  goalEndDate: "deadline", averageDealSize: "deal size", bestCustomers: "best customers", mission: "mission" };

const Bubble = ({ m, onSuggest }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}`}>
    <div className={`max-w-[92%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-ism-teal/20 text-white rounded-br-md" : "bg-white/[0.05] text-gray-200 rounded-bl-md"}`}>
      <p className="whitespace-pre-wrap">{m.content}</p>
      {m.applied?.length > 0 && (
        <p className="mt-2 text-[11px] text-emerald-300 flex items-start gap-1" data-testid="assistant-applied"><Check className="h-3 w-3 mt-0.5 flex-shrink-0" /> Updated {m.applied.map((k) => LABELS[k] || k).join(", ")}</p>
      )}
      {m.suggestions?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {m.suggestions.map((s) => (
            <button key={s} onClick={() => onSuggest(s)} className="text-[11px] px-2.5 py-1 rounded-full border border-white/10 text-gray-300 hover:border-ism-teal/40 hover:text-ism-teal transition-colors" data-testid="assistant-suggestion">{s}</button>
          ))}
        </div>
      )}
    </div>
  </div>
);

const Composer = ({ onSend, busy }) => {
  const [text, setText] = useState("");
  const submit = () => { if (!text.trim()) return; onSend(text); setText(""); };
  return (
    <div className="flex items-end gap-2 border-t border-white/10 p-3">
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Tell me in your own words…"
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:border-ism-teal/50 focus:outline-none resize-none max-h-28"
        data-testid="assistant-input" />
      <button onClick={submit} disabled={busy || !text.trim()} className="h-9 w-9 rounded-xl bg-ism-teal text-black flex items-center justify-center disabled:opacity-40" aria-label="Send" data-testid="assistant-send-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </button>
    </div>
  );
};

export const OnboardingAssistant = ({ userId, step, data, patch, patchSpec }) => {
  const a = useOnboardingAssistant({ userId, step, data, patch, patchSpec });
  const [open, setOpen] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [a.thread.length, a.busy]);

  const panel = (
    <div className="flex flex-col h-full rounded-2xl border border-white/10 bg-[#0f1419]/90 shadow-2xl overflow-hidden" data-testid="onboarding-assistant">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 text-sm text-white"><Bot className="h-4 w-4 text-ism-teal" /> Setup guide <span className="text-[10px] uppercase tracking-wider text-gray-500 ml-1">{a.stepId}</span></div>
        <button onClick={() => setOpen(false)} className="lg:hidden text-gray-500 hover:text-white" aria-label="Close" data-testid="assistant-close-btn"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0" data-testid="assistant-thread">
        {a.thread.map((m, i) => <Bubble key={`${a.stepId}-${i}-${m.role}`} m={m} onSuggest={a.send} />)}
        {a.busy && <div className="flex items-center gap-2 text-xs text-gray-500 pl-1"><Loader2 className="h-3 w-3 animate-spin" /> thinking…</div>}
        <div ref={endRef} />
      </div>
      <Composer onSend={a.send} busy={a.busy} />
    </div>
  );

  return (
    <>
      <div className="hidden lg:block lg:sticky lg:top-8 h-[calc(100vh-8rem)] max-h-[720px]">{panel}</div>
      <button onClick={() => setOpen(true)} className="lg:hidden fixed bottom-5 right-5 z-[60] h-12 w-12 rounded-full bg-ism-teal text-black shadow-xl flex items-center justify-center" aria-label="Open setup guide" data-testid="assistant-open-btn">
        <MessageSquare className="h-5 w-5" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "tween", duration: 0.25 }} className="lg:hidden fixed inset-x-0 bottom-0 z-[70] h-[78vh] p-3">
            {panel}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
